import React from "react"
import { PageProps } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import * as styles from "./brand-affinity.module.scss"

import SEO from "../../../components/seo"
import ProjectLayout from "../../../templates/project-page/ProjectLayout"
import ProjectGrid from "../../../components/UI/ProjectGrid"
import Image from "../../../components/UI/Image"


const NexusMentis: React.FC<PageProps> = ({ location }) => {
	const intl = useIntl()

	return (
		<ProjectLayout
			title={`Brand Affinity: \nPlatform for Brand Compliance Monitoring, Analytics and Audit Automation `}
			date="2019"
			desktopImage="/images/projects/brand-affinity/ba-title.png"
			description="After Majid AL Futtaim consolidation in 2014, the company  was looking for a brand affinity platform for regular brand compliance monitoring and analytics to ensure that all brand standards are kept at the highest level in all the 17 countries the holding operates in."
			client="Majid Al Futtaim (UAE)"
			expretise={`Full-cycle development \nDesign support \nAnalytics`}
			projectImage="/images/projects/brand-affinity/ba.png"
			location={location}
			back="white"
		>
			<SEO
				language={intl.locale}
				description={intl.formatMessage({ id: "projects-new.list.ba.description" })}
				title={intl.formatMessage({ id: "projects-new.list.ba.metatitle" })}
				pathname={location.pathname}
			/>
			{/* Дополнительный контент */}
			<section className={styles.objective}>
				<h2>Objective</h2>
				<div className={styles.objectiveSubtitle}>
					<p>
						Develop a platform for centralized management of brand audits, communications, tasks and analytics.
					</p>
				</div>
				<h3>The system had to be:</h3>
				<ProjectGrid
					number
					listItems={[
						{ title: "Centrally monitor visual and content compliance with brand guidelines." },
						{ title: "Simplify and automate the compliance audit process." },
						{ title: "Provide analytical data on the effectiveness of brand guideline enforcement." },
					]}
				/>
				<div className={styles.objectiveImg}><Image src="/images/projects/brand-affinity/design-process.png" alt="Design process" fill /></div>
			</section>
			<section className={styles.solution}>
				<h2>Solution</h2>

				<div className={styles.solutionSubtitle}>
					<p>
						We have created a custom desktop system that includes the following key modules:
					</p>
				</div>
				<div className={styles.solutionTextBlock}>
					<h3>Monitoring Dashboard</h3>
					<p>A user-friendly interface with an overview of key metrics, user activity, progress on audits and communications. The dashboard adapts to the user's role, displaying only relevant information.</p>
				</div>
				<div className={styles.solutionImg}><Image src="/images/projects/brand-affinity/solution-1.png" alt="Good morning" fill /></div>
				<h3>Audit Module</h3>
				<ProjectGrid
					high
					listItems={[
						{ title: "Create and customize brand audits for specific tasks and standards." },
						{ title: "Support audit templates to unify processes and quickly launch audits." },
						{ title: "Assign audits to companies, departments and users." },
						{ title: "Ability to upload photos directly from mobile devices during an audit, with automatic geo-positioning of the object." },
						{ title: "Visual comparison tool: allows you to mark deficiencies directly on the image (e.g., discrepancies in display, violation of guideline, etc.)." },
					]}
				/>
				<div className={styles.solutionImg}><Image src="/images/projects/brand-affinity/solution-2.png" alt="Audit: busuness card" fill /></div>
				<div className={styles.solutionTextBlock}>
					<h3>A tool for creating libraries of design assets</h3>
					<p>Centralized repository of brand materials: logos, colors, fonts, templates and guidelines. All elements are available to auditors for quick reconciliation and standards compliance.</p>
				</div>
				<div className={styles.solutionImg}><Image src="/images/projects/brand-affinity/solution-3.png" alt="Assets library" fill /></div>
				<h3>Analytics and reporting</h3>
				<ProjectGrid
					high
					listItems={[
						{ title: "Interactive reports on audit results." },
						{ title: "Flexible data filtering, visualization, export to Excel and PDF." },
						{ title: "Support for custom dashboards by role and access level." },
					]}
				/>
				<h3 className={styles.taskTitle}>Task and communication management</h3>
				<ProjectGrid
					high
					listItems={[
						{ title: "Ability to comment and task audits." },
						{ title: "System of notifications and statuses to monitor the implementation of corrective actions." },
					]}
				/>
			</section>
			<section className={styles.result}>
				<h2>Result</h2>
				<div className={styles.resultSubtitle}>
					<p>The project was a key step in the digital transformation of brand management and brand presentation standards. We created more than just a platform - we laid the foundation for scalable control, high transparency of processes and increased responsibility of teams for results at every point of interaction with the brand.</p>
				</div>
				<ProjectGrid
					bg="purple"
					rows
					listItems={[
						{ percent: "-68%", title: "Reduced burden on administrators - automation of routine tasks reduced manual work" },
						{ percent: "+35%", title: "Increased accuracy of brand control, in the first 3 months - thanks to a single repository of asserts and a clear structure of processes" },
						{ title: "Centralization of brand audits in one system for all regions and teams." },
						{ title: "Improved transparency - now clients can see progress on tasks and audits in real time. " },
						{ title: "Flexible scalability - the system is adaptable to companies of any size, from local representatives to global corporations." },
					]}
				/>
			</section>
		</ProjectLayout >

	)
}

export default NexusMentis;
